<template>
  <div class="omnisearch">
    <label class="text-white">{{ t('jobs_list.omni_search.seach_by') }}</label>
    <div class="input-wrapper">
      <input
        type="text"
        class="form-control"
        :placeholder="t('jobs_list.omni_search.placeholder')"
        aria-label="Search"
        :disabled="loading"
        v-model="search"
        @keydown.down.prevent="focusNext"
        @keydown.up.prevent="focusPrev"
        @keydown.enter.prevent="onEnter"
        @keydown.esc.prevent="handleClose"
        ref="searchInput"
      />
      <span
        v-if="search"
        :class="`material-symbols-outlined icon ${
          loading ? 'loading-icon' : ''
        }`"
        @click="clearSearch"
      >
        close
      </span>
      <span
        v-else
        :class="`material-symbols-outlined icon ${
          loading ? 'loading-icon' : ''
        }`"
        >search</span
      >
    </div>

    <!-- Filter Controls -->
    <div class="filter-controls mt-2">
      <div class="d-flex gap-3 align-items-center">
        <label class="text-white text-small mb-0"
          >{{ t('jobs_list.omni_search.filters') }}:</label
        >
        <div class="form-check form-check-inline">
          <input
            class="form-check-input"
            type="checkbox"
            id="ignoreCanceled"
            v-model="ignoreCanceled"
          />
          <label
            class="form-check-label text-white text-small"
            for="ignoreCanceled"
          >
            {{ t('jobs_list.omni_search.ignore_canceled') }}
          </label>
        </div>
        <div class="form-check form-check-inline">
          <input
            class="form-check-input"
            type="checkbox"
            id="ignoreCompleted"
            v-model="ignoreCompleted"
          />
          <label
            class="form-check-label text-white text-small"
            for="ignoreCompleted"
          >
            {{ t('jobs_list.omni_search.ignore_completed') }}
          </label>
        </div>
      </div>
    </div>

    <ul
      class="form-control"
      v-if="search && search.length"
      @scroll="onScroll"
    >
      <template v-if="results?.length">
        <li
          v-for="(result, index) in results"
          :key="result.short_code"
          class="d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between"
          @click="openJob(result)"
          :class="{ highlight: activeIndex === index }"
        >
          <div class="d-flex flex-column">
            <div class="d-flex mb-1">
              <h5
                class="mb-0 me-2"
                :class="{ 'text-strike': result.is_canceled }"
              >
                {{ result.full_name }}
              </h5>

              <span class="text-small">{{ result.short_code }}</span>
            </div>
            <div class="d-flex text-small">
              <div class="d-flex align-items-center me-2">
                <span class="material-symbols-outlined me-1 fs-1">
                  location_on
                </span>
                {{ result.address }}
              </div>

              <div class="d-flex align-items-center me-2">
                <span class="material-symbols-outlined me-1 fs-1">
                  handyman
                </span>
                {{ result.contractor.name }}
              </div>
            </div>
          </div>
          <div class="d-flex flex-column gap-1 align-items-end">
            <Badge
              class=""
              :label="
                result.is_canceled
                  ? t('common_labels.canceled')
                  : t(`common_job.stage_by_number.${result.stage}`)
              "
              variant="light-gray"
            />
            <div
              v-if="result.active_job_programs"
              class="d-flex gap-2"
            >
              <div
                v-for="jp in result.active_job_programs"
                :key="jp.id"
              >
                <span class="d-flex">
                  <Badge variant="info-light">
                    <ProgramNameShort
                      :programKey="jp.program.key"
                      variant="sm"
                    />
                  </Badge>
                  <template v-if="isInternal">
                    <CopyableExternalIdPill
                      v-if="
                        [JobStageMap.ENROLL, JobStageMap.COMPLETE].includes(
                          result.stage
                        ) && jp.details.external_ids?.enrollment_id
                      "
                      class="ms-1"
                      :id="jp.details.external_ids?.enrollment_id"
                      :successMessage="
                        t(
                          'common_messages.success.enrollment_id_copied_to_clipboard',
                          {
                            id: jp.details.external_ids?.enrollment_id
                          }
                        )
                      "
                    />
                    <CopyableExternalIdPill
                      class="mx-1"
                      v-else-if="jp.details.external_ids?.preapproval_id"
                      :id="jp.details.external_ids.preapproval_id"
                      :successMessage="
                        t(
                          'common_messages.success.preapproval_id_copied_to_clipboard',
                          {
                            id: jp.details.external_ids.preapproval_id
                          }
                        )
                      "
                    />
                  </template>
                </span>
              </div>
            </div>
          </div>
        </li>
      </template>
      <template v-if="loading">
        <li class="text-center text-small">
          <div><Spinner class="rotate" /></div>
        </li>
      </template>
      <template v-else-if="search && !loading && !results?.length">
        <li class="text-center text-small">
          <div>
            {{ t('jobs_list.omni_search.no_results_found_warning') }}
          </div>
          <div>
            {{ t('jobs_list.omni_search.search_by_another_param_warning') }}
          </div>
        </li>
      </template>
    </ul>
  </div>
  <div
    class="omnisearch-backdrop"
    @click="handleClose"
  ></div>
</template>

<script setup lang="ts">
import Badge from '@shared/components/Badge.vue';
import CopyableExternalIdPill from '@shared/components/CopyableExternalIdPill.vue';
import ProgramNameShort from '@shared/components/ProgramNameShort.vue';
import Spinner from '@shared/components/Spinner.vue';
import { type JobSearchResponseType } from '@shared/generated';
import { JobStageMap } from '@shared/types/stage';
import { nextTick, onBeforeMount, onBeforeUnmount, watch, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';

const props = defineProps<{
  results?: JobSearchResponseType[];
  loading?: boolean;
}>();
const { t } = useI18n();

const search = defineModel<string>('search');
const ignoreCanceled = defineModel<boolean>('ignoreCanceled');
const ignoreCompleted = defineModel<boolean>('ignoreCompleted');

const emit = defineEmits(['close', 'openJob', 'clearSearch', 'loadMore']);
const searchInput = ref<HTMLInputElement>();
const route = useRoute();
const isInternal = Boolean(route.meta.isInternal);

onBeforeMount(() => {
  document.getElementsByTagName('body')[0].classList.add('scroll-disabled');
});

onBeforeUnmount(() => {
  document.getElementsByTagName('body')[0].classList.remove('scroll-disabled');
});

watch(
  () => props.loading,
  (newLoadingVal) => {
    if (!newLoadingVal) {
      // Focus the input after loading completes
      nextTick(() => {
        searchInput.value?.focus();
      });
    }
  }
);

const activeIndex = ref(0);
const focusNext = () => {
  if (!props.results?.length) return;
  if (activeIndex.value === undefined) {
    activeIndex.value = 0;
  } else if (activeIndex.value < props.results.length - 1) {
    activeIndex.value++;
  }
};

const focusPrev = () => {
  if (activeIndex.value === undefined) {
    activeIndex.value = 0;
  } else if (activeIndex.value > 0) {
    activeIndex.value--;
  }
};

const onEnter = () => {
  if (
    activeIndex.value !== undefined &&
    props.results &&
    props.results[activeIndex.value]
  ) {
    openJob(props.results[activeIndex.value]);
  }
};

const openJob = (job: JobSearchResponseType) => {
  emit('openJob', job.id);
  emit('close');
};

const clearSearch = () => {
  emit('clearSearch');
};

const handleClose = () => {
  emit('close');
};

const onScroll = (event: Event) => {
  const target = event.target as HTMLElement;
  if (target.scrollTop + target.clientHeight >= target.scrollHeight - 10) {
    emit('loadMore');
  }
};
</script>

<style lang="scss" scoped>
$omnisearch-top-desktop: 200px;
$omnisearch-top-mobile: 100px;
$omnisearch-list-margin: 0.75rem;
$omnisearch-container-width: 700px;

// magic number to account for height of label + input
$omnisearch-input-height: 72px;

.omnisearch-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  background-color: rgba(113, 113, 111, 0.8);
  backdrop-filter: blur(3px);
  z-index: 10001; // 1 higher than header component
}
.omnisearch {
  position: fixed;
  top: $omnisearch-top-desktop;
  z-index: 10002; // 1 higher than backdrop
  left: 0;
  right: 0;
  margin: 0 auto;
  width: 100%;
  max-width: $omnisearch-container-width;
  @media screen and (max-width: 768px) {
    top: $omnisearch-top-mobile;
    max-width: calc(100vw - 1rem);
  }

  .input-wrapper {
    position: relative;
    .icon {
      color: $brand-blue;
      position: absolute;
      top: 50%;
      transform: translateY(-50%);
      right: 1rem;
    }
    .loading-icon {
      color: $gray-48;
    }
  }

  ul {
    list-style: none;
    margin: $omnisearch-list-margin 0 0;
    padding: 0;
    max-height: calc(
      100vh -
        (
          #{$omnisearch-top-desktop} + #{$omnisearch-list-margin} + #{$omnisearch-input-height} +
            1rem
        )
    );
    overflow-y: auto;
    overflow-x: hidden;

    @media screen and (max-width: 768px) {
      max-height: calc(
        100vh -
          (
            #{$omnisearch-top-mobile} + #{$omnisearch-list-margin} + #{$omnisearch-input-height} +
              1rem
          )
      );
    }

    li {
      padding: 1rem;
      border-bottom: 1px solid #e0e0e0;
      cursor: pointer;
      &:last-child {
        border-bottom: none;
      }
      &:hover,
      &.highlight {
        background-color: #f5f5f5;
      }
    }
  }
}
</style>
